import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Calendar, Video, CalendarClock, XCircle, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

function formatSlot(iso) {
  if (!iso) return "";
  const d = new Date(iso.includes("Z") ? iso : iso + ":00");
  return d.toLocaleString("en-US", { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" });
}
function formatDate(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}
function formatTime(t) {
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hr = h % 12 || 12;
  return `${hr}:${String(m).padStart(2, "0")} ${ampm}`;
}

export default function ManageBooking() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("view"); // view | reschedule | cancelled
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/bookings/manage/${token}`).then((res) => setData(res.data)).catch((e) => setError(formatApiError(e.response?.data?.detail) || "Booking not found"));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!token) { setError("No booking token provided."); return; }
    load();
  }, [token]);

  const openReschedule = () => {
    setMode("reschedule");
    api.get("/availability/slots").then((res) => setSlots(res.data.slots));
  };

  const doReschedule = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/bookings/manage/${token}/reschedule`, { slot: selected.datetime });
      toast.success("Your call has been rescheduled");
      setMode("view");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not reschedule");
    } finally {
      setBusy(false);
    }
  };

  const doCancel = async () => {
    if (!window.confirm("Are you sure you want to cancel this consultation?")) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/bookings/manage/${token}/cancel`);
      toast.success("Your call has been cancelled");
      setMode("cancelled");
      setData((d) => ({ ...d, booking: { ...d.booking, status: "cancelled" }, refund_eligible: res.refund_eligible }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not cancel");
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div data-testid="page-manage-booking" className="mx-auto max-w-xl px-6 py-28 text-center">
        <XCircle className="mx-auto text-destructive" size={48} strokeWidth={1.5} />
        <h1 className="mt-6 font-serif text-2xl text-charcoal-800">{error}</h1>
        <Link to="/" className="mt-6 inline-flex rounded-full bg-terracotta px-7 py-3 text-white font-medium hover:bg-terracotta-hover transition-colors">Back home</Link>
      </div>
    );
  }

  if (!data) return <div className="mx-auto max-w-xl px-6 py-28 text-charcoal-500">Loading…</div>;

  const b = data.booking;
  const byDate = slots.reduce((acc, s) => { (acc[s.date] = acc[s.date] || []).push(s); return acc; }, {});

  return (
    <div data-testid="page-manage-booking" className="mx-auto max-w-2xl px-6 lg:px-12 py-16 lg:py-24">
      <p className="text-sm uppercase tracking-[0.2em] text-clay-700 font-medium mb-4">Manage your booking</p>
      <h1 className="font-serif text-3xl sm:text-4xl font-light text-charcoal-900">Your intro consultation</h1>

      <div className="mt-8 rounded-3xl bg-white p-8 border border-sand-200 space-y-4">
        <div className="flex items-center gap-3">
          <Calendar className="text-terracotta" strokeWidth={1.5} />
          <span data-testid="manage-slot" className="text-charcoal-800 font-medium">{formatSlot(b.slot)}</span>
        </div>
        <div className="flex items-center gap-3">
          <Video className="text-terracotta" strokeWidth={1.5} />
          <span className="text-charcoal-700">{b.platform}{b.meeting_link ? ` · ${b.meeting_link}` : ""}</span>
        </div>
        <span className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${b.status === "cancelled" ? "bg-charcoal-100 text-charcoal-500" : "bg-terracotta-light text-terracotta-700"}`}>
          {b.status}
        </span>
      </div>

      {b.status === "cancelled" || mode === "cancelled" ? (
        <div data-testid="manage-cancelled" className="mt-6 rounded-3xl bg-sage-light p-8 text-center">
          <CheckCircle2 className="mx-auto text-sage" size={40} strokeWidth={1.5} />
          <p className="mt-3 text-charcoal-700">
            This booking is cancelled. {data.refund_eligible ? "You're eligible for a full refund." : "No refund applies per the policy below."}
          </p>
        </div>
      ) : mode === "view" ? (
        <div className="mt-8 space-y-6">
          <div className="flex flex-wrap gap-4">
            <button data-testid="manage-reschedule-open" onClick={openReschedule} className="inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3 text-white font-medium hover:bg-terracotta-hover transition-colors">
              <CalendarClock size={18} /> Reschedule
            </button>
            <button data-testid="manage-cancel" onClick={doCancel} disabled={busy} className="inline-flex items-center gap-2 rounded-full border border-charcoal-300 px-7 py-3 text-charcoal-800 font-medium hover:bg-sand-200 transition-colors">
              <XCircle size={18} /> Cancel booking
            </button>
          </div>
          <div className="rounded-2xl bg-white p-6 border border-sand-200 text-sm text-charcoal-600 leading-relaxed space-y-2">
            <p><b className="text-charcoal-800">Reschedule:</b> {data.reschedule_policy}</p>
            <p><b className="text-charcoal-800">Refunds:</b> {data.refund_policy}</p>
          </div>
        </div>
      ) : (
        <div className="mt-8">
          <button onClick={() => setMode("view")} className="inline-flex items-center gap-1 text-sm text-charcoal-500 hover:text-terracotta mb-4">
            <ArrowLeft size={15} /> Back
          </button>
          <div className="rounded-3xl bg-white p-8 border border-sand-200">
            <h2 className="text-xl font-medium text-charcoal-800 mb-6">Pick a new time</h2>
            <div className="space-y-6 max-h-[400px] overflow-y-auto pr-2">
              {Object.entries(byDate).map(([date, daySlots]) => (
                <div key={date}>
                  <p className="text-sm font-medium text-charcoal-700 mb-3">{formatDate(date)}</p>
                  <div className="flex flex-wrap gap-2">
                    {daySlots.map((s) => (
                      <button
                        key={s.datetime}
                        data-testid={`manage-slot-${s.datetime}`}
                        onClick={() => setSelected(s)}
                        className={`rounded-full px-4 py-2 text-sm font-medium transition-colors border ${
                          selected?.datetime === s.datetime ? "bg-terracotta text-white border-terracotta" : "bg-sand-100 text-charcoal-700 border-sand-200 hover:border-terracotta"
                        }`}
                      >
                        {formatTime(s.time)}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <button data-testid="manage-reschedule-confirm" onClick={doReschedule} disabled={!selected || busy} className="mt-6 rounded-full bg-terracotta px-7 py-3 text-white font-medium hover:bg-terracotta-hover transition-colors disabled:opacity-50">
              {busy ? "Rescheduling…" : "Confirm new time"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
